import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { emojiMap, emojis as exportedEmojis } from './emoji';
import { Editor, Toolbar } from 'ngx-editor';

@Component({
    selector: 'app-text-editor',
    templateUrl: './text-editor.component.html',
    styleUrls: ['./text-editor.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class TextEditorComponent implements OnInit, AfterViewInit {
    @Input() data = '';
    @Output() outputData = new EventEmitter<string>();
    outputDelay?: any;

    emojis = exportedEmojis;
    emojiArray = Object.entries(emojiMap);

    // Custom link dialog properties
    linkUrl = '';
    linkText = '';

    toolbar: Toolbar = [
        ['bold', 'italic'],
        [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
        ['ordered_list', 'bullet_list'],
        ['align_left', 'align_center', 'align_right', 'align_justify'],
    ];
    editorNgx?: Editor;
    editorContentGetter?: any;
    @ViewChild('editorMenu') editorMenu!: ElementRef;
    @ViewChild('emojiButton') emojiButton!: ElementRef;
    @ViewChild('linkButton') linkButton!: ElementRef;
    @ViewChild('linkCheckbox') linkCheckbox!: ElementRef;

    ngOnInit(): void {
        this.editorNgx = new Editor();
        this.data = this.processDescription(this.data ?? '');
        this.editorContentGetter = this.data;
    }

    ngAfterViewInit(): void {
        this.editorMenu.nativeElement.querySelector('.NgxEditor__MenuBar').appendChild(this.emojiButton.nativeElement);
        this.editorMenu.nativeElement.querySelector('.NgxEditor__MenuBar').appendChild(this.linkButton.nativeElement);

        // Add click outside listener to close link dialog
        document.addEventListener('click', event => {
            if (this.linkCheckbox.nativeElement.checked) {
                const target = event.target as Element;
                // Check if click is outside the link button and its dialog
                if (!target.closest('.linkButton') && !target.closest('.link-dialog')) {
                    this.closeLinkDialog();
                }
            }
        });
    }

    processDescription(description: string): string {
        const pattern = /:(\w+):/g;
        // description = description.replace(/\n/g, '<br>');
        return description.replace(pattern, (match, p1) => {
            return emojiMap[`:${p1}:`] || match;
        });
    }

    onChange(value: any): void {
        if (this.outputDelay) {
            clearTimeout(this.outputDelay);
        }
        this.outputDelay = setTimeout(() => this.outputData.emit(value), 500);
    }

    insertNgxEmoji(emoji: string): void {
        this.editorNgx?.commands.insertText(emoji).exec();
    }

    onPaste(event: ClipboardEvent) {
        const items = event.clipboardData?.items;
        if (!items) return;

        const hasImageFile = Array.from(items).some(item => item.type.startsWith('image'));
        const hasHtml = Array.from(items).some(item => item.type === 'text/html');

        if (hasImageFile && !hasHtml) {
            event.preventDefault();
            event.stopPropagation();

            for (const item of Array.from(items)) {
                if (item.type.startsWith('image')) {
                    const file = item.getAsFile();
                    if (file) {
                        const reader = new FileReader();
                        reader.onload = () => {
                            const base64Image = reader.result as string;
                            this.insertImage(base64Image);
                        };
                        reader.readAsDataURL(file);
                    }
                    break;
                }
            }
        }
    }

    insertImage(base64Image: string) {
        const imgTag = `<img src="${base64Image}" style="max-width: 100%;" />`;
        document.execCommand('insertHTML', false, imgTag);
    }

    insertCustomLink(): void {
        if (this.linkUrl.trim()) {
            const url = this.linkUrl.trim();
            const text = this.linkText.trim() || url;

            const linkHtml = `<a href="${url}" target="_blank">${text}</a>`;
            this.editorNgx?.commands.insertHTML(linkHtml).exec();

            // Close the dialog and clear inputs
            this.closeLinkDialog();
        }
    }

    closeLinkDialog(): void {
        this.linkCheckbox.nativeElement.checked = false;
        this.linkUrl = '';
        this.linkText = '';
    }
}
